import type { Session } from '@supabase/supabase-js';
import type { Cliente } from '@sheafyield/sync';
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { GrupoDeChips } from '../ds/GrupoDeChips.js';
import {
  ehTolerancia,
  useTrava,
  trava as travaDoApp,
  type ArmazemDaPreferencia,
  type Tolerancia,
  type Trava,
} from '../seguranca/trava.js';
import { ORIGEM_DE_PRODUCAO } from '../supabase/config.js';
import './ajustes.css';

/** "Nunca" é o desligado; as outras três são a tolerância. Um grupo de
 *  rádios só, e não um interruptor mais um seletor: são quatro respostas à
 *  mesma pergunta ("quando pedir o Face ID?"), e em AX5 um controle a menos
 *  é uma tela a menos de rolagem. */
const QUANDO: ReadonlyArray<{ valor: 'nunca' | Tolerancia; rotulo: string }> = [
  { valor: 'nunca', rotulo: 'Nunca' },
  { valor: 'imediatamente', rotulo: 'Ao abrir' },
  { valor: '1min', rotulo: 'Depois de 1 minuto' },
  { valor: '5min', rotulo: 'Depois de 5 minutos' },
];

/**
 * Quando o app pede o Face ID para se abrir.
 *
 * Fica na seção Conta porque é a mesma chave-senha do login — a que o
 * usuário registrou esperando exatamente isto.
 */
function BloqueioDoApp({
  trava,
  armazem,
}: {
  trava: Trava;
  armazem: ArmazemDaPreferencia;
}) {
  const estado = useTrava(trava);
  return (
    <GrupoDeChips
      nome="trava"
      rotulo="Bloquear o app com Face ID"
      ajuda="Pede o Face ID ao abrir e ao voltar do segundo plano. Vale só neste aparelho e não criptografa nada: protege de quem pega o telefone, não de quem o examina."
      opcoes={QUANDO}
      valor={estado.ligada ? estado.tolerancia : 'nunca'}
      aoMudar={(v) => {
        // `configurar` publica o estado novo antes de gravar; a gravação no
        // Dexie é o que sobra da promessa, e não há o que esperar na tela.
        void trava.configurar(
          ehTolerancia(v)
            ? { ligada: true, tolerancia: v }
            : { ligada: false, tolerancia: estado.tolerancia },
          armazem,
        );
      }}
    />
  );
}

export interface DepsDaConta {
  cliente: Cliente;
  sessao: Session | null;
  /** `location.origin` da janela; injetado para o teste cobrir os dois lados. */
  origem: string;
  pedirCodigo: (c: Cliente, email: string) => Promise<void>;
  confirmarCodigo: (c: Cliente, email: string, codigo: string) => Promise<void>;
  sair: (c: Cliente) => Promise<void>;
  passkeysDisponiveis: (c: Cliente) => boolean;
  registrarPasskey: (c: Cliente) => Promise<void>;
  /** Quantas passkeys a conta tem; a tela usa para dizer se já pegou. */
  listarPasskeys: (c: Cliente) => Promise<number>;
  entrarComPasskey: (c: Cliente) => Promise<void>;
  /** A trava local do app. A do app é o padrão; o teste passa a sua. */
  trava?: Trava;
  /** Onde a preferência da trava é gravada: a tabela `estado` do Dexie.
   *  Exigido, e não herdado do que a trava guardou em `carregar`, porque
   *  uma tela que liga a opção sem ter onde gravá-la mente para o usuário —
   *  mostra ligado e esquece no recarregamento. */
  armazemDaTrava: ArmazemDaPreferencia;
}

export function Conta({ deps }: { deps: DepsDaConta }) {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [codigoPedido, setCodigoPedido] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  // Passkey só na origem de produção: é onde o RP ID vale (spec §5). Em
  // `localhost` e em pré-visualização de PR o botão apareceria e falharia
  // com o erro cru do GoTrue, que `traduzirErro` não traduz.
  const passkeys =
    deps.passkeysDisponiveis(deps.cliente) &&
    deps.origem === ORIGEM_DE_PRODUCAO;
  // `null` = não sabemos (ainda carregando, ou a contagem falhou por falta
  // de rede). Sem rede o usuário não fez nada errado: a tela mostra só o
  // botão, sem erro.
  const [chaves, setChaves] = useState<number | null>(null);
  const podeContar = passkeys && deps.sessao !== null;

  // Depende das duas funções que usa, não do objeto `deps` inteiro: assim o
  // efeito não obriga quem monta a tela a memoizar o objeto (um chamador que
  // esquecesse viraria uma chamada a `/passkeys` a cada empurrão de estado
  // do motor).
  const listar = deps.listarPasskeys;
  const cliente = deps.cliente;
  const contar = useCallback(
    (): Promise<number | null> =>
      listar(cliente).then(
        (n) => n,
        () => null,
      ),
    [listar, cliente],
  );

  useEffect(() => {
    if (!podeContar) return;
    let vivo = true;
    void contar().then((n) => {
      if (vivo) setChaves(n);
    });
    return () => {
      vivo = false;
    };
  }, [contar, podeContar]);
  // Derivado, não guardado: sair da conta não precisa de um `setState` no
  // efeito só para zerar a contagem.
  const chavesVisiveis = podeContar ? chaves : null;

  // A trava só se oferece a quem tem chave para abri-la: ligá-la sem
  // nenhuma passkey registrada trancaria o app com uma porta sem chave, e
  // "tentar de novo" nunca daria certo. Uma vez ligada ela continua à vista
  // mesmo sem rede (quando a contagem falha e vem `null`) — senão, quem
  // quisesse desligá-la offline não teria onde.
  const trava = deps.trava ?? travaDoApp;
  const estadoDaTrava = useTrava(trava);
  const podeBloquear =
    passkeys && (estadoDaTrava.ligada || (chavesVisiveis ?? 0) > 0);

  async function executar(acao: () => Promise<void>, sucesso?: string) {
    setOcupado(true);
    setErro(null);
    setAviso(null);
    try {
      await acao();
      if (sucesso) setAviso(sucesso);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setOcupado(false);
    }
  }

  function pedir(ev: FormEvent) {
    ev.preventDefault();
    void executar(async () => {
      await deps.pedirCodigo(deps.cliente, email.trim());
      setCodigoPedido(true);
    }, 'Código enviado. Confira seu e-mail.');
  }
  function confirmar(ev: FormEvent) {
    ev.preventDefault();
    void executar(() =>
      deps.confirmarCodigo(deps.cliente, email.trim(), codigo),
    );
  }

  return (
    <section className="ajustes__secao" aria-labelledby="conta">
      <h2 id="conta">Conta</h2>
      {deps.sessao ? (
        <>
          <p>
            Conectado como <strong>{deps.sessao.user.email}</strong>.
          </p>
          {passkeys && (
            <>
              {/* "nesta conta", não "neste aparelho": a contagem vem do
                  servidor e soma as chaves de todos os aparelhos. */}
              {chavesVisiveis !== null && chavesVisiveis > 0 && (
                <p>
                  Face ID ativo · {chavesVisiveis}{' '}
                  {chavesVisiveis === 1 ? 'chave' : 'chaves'} nesta conta. Serve
                  para entrar sem digitar o código quando a sessão acabar ou em
                  outro aparelho — e, se você ligar o bloqueio abaixo, para
                  abrir o app.
                </p>
              )}
              <Botao
                variante="secundario"
                largura="cheia"
                disabled={ocupado}
                onClick={() =>
                  void executar(async () => {
                    await deps.registrarPasskey(deps.cliente);
                    setChaves(await contar());
                  }, 'Face ID ativado neste aparelho.')
                }
              >
                {chavesVisiveis !== null && chavesVisiveis > 0
                  ? 'Adicionar outra chave'
                  : 'Entrar sem código com Face ID neste aparelho'}
              </Botao>
            </>
          )}
          <Botao
            variante="secundario"
            largura="cheia"
            disabled={ocupado}
            onClick={() => void executar(() => deps.sair(deps.cliente))}
          >
            Sair
          </Botao>
        </>
      ) : (
        <>
          <p>
            Sem conta o app funciona inteiro; a conta só serve para sincronizar
            entre aparelhos.
          </p>
          {passkeys && (
            <Botao
              variante="secundario"
              largura="cheia"
              disabled={ocupado}
              onClick={() =>
                void executar(() => deps.entrarComPasskey(deps.cliente))
              }
            >
              Entrar com Face ID
            </Botao>
          )}
          {!codigoPedido ? (
            <form onSubmit={pedir}>
              <Campo
                rotulo="E-mail"
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Botao type="submit" largura="cheia" disabled={ocupado}>
                Enviar código
              </Botao>
            </form>
          ) : (
            <form onSubmit={confirmar}>
              <Campo
                rotulo="Código de 8 dígitos"
                id="codigo"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{8}"
                maxLength={8}
                required
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
              />
              <Botao type="submit" largura="cheia" disabled={ocupado}>
                Entrar
              </Botao>
              <Botao
                variante="texto"
                largura="cheia"
                onClick={() => setCodigoPedido(false)}
              >
                Trocar e-mail
              </Botao>
            </form>
          )}
        </>
      )}
      {podeBloquear && (
        <BloqueioDoApp trava={trava} armazem={deps.armazemDaTrava} />
      )}
      <p role="status" className="ajustes__status">
        {aviso}
      </p>
      {erro && (
        <p role="alert" className="ajustes__erro">
          {erro}
        </p>
      )}
    </section>
  );
}
